import {
  TrashIcon,
  CurrencyDollarIcon,
  TagIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  XIcon,
  CalendarIcon,
  MapPinIcon,
  PackageIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type {
  SuggestedAction,
  SuggestedActionType,
  SuggestedActionStatus,
  SuggestedActionWithStatus,
} from '@erp/shared-types';

export type { SuggestedActionStatus, SuggestedActionWithStatus };

interface SuggestedActionCardProps {
  action: SuggestedActionWithStatus;
  onApply: (action: SuggestedActionWithStatus) => void;
  onDismiss: (action: SuggestedActionWithStatus) => void;
  onReviewDraft?: (action: SuggestedActionWithStatus) => void;
  isApplying?: boolean;
  className?: string;
}

/** Action types that target a specific line item and should show the SKU */
const lineItemActionTypes = new Set<SuggestedActionType>([
  'remove_line_item',
  'update_line_item_price',
  'create_discount',
  'update_line_item_product',
]);

const actionTypeConfig = {
  remove_line_item: {
    icon: TrashIcon,
    label: 'Remove Line Item',
    iconColor: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-950',
    borderColor: 'border-red-200 dark:border-red-800',
  },
  update_line_item_price: {
    icon: CurrencyDollarIcon,
    label: 'Update Price',
    iconColor: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-950',
    borderColor: 'border-amber-200 dark:border-amber-800',
  },
  create_discount: {
    icon: TagIcon,
    label: 'Create Discount',
    iconColor: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-950',
    borderColor: 'border-green-200 dark:border-green-800',
  },
  send_email_draft: {
    icon: EnvelopeIcon,
    label: 'Send Email',
    iconColor: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-950',
    borderColor: 'border-blue-200 dark:border-blue-800',
  },
  approve_order: {
    icon: CheckCircleIcon,
    label: 'Approve Order',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950',
    borderColor: 'border-emerald-200 dark:border-emerald-800',
  },
  update_order_dates: {
    icon: CalendarIcon,
    label: 'Set Dates',
    iconColor: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-950',
    borderColor: 'border-purple-200 dark:border-purple-800',
  },
  update_ship_to_location: {
    icon: MapPinIcon,
    label: 'Set Location',
    iconColor: 'text-teal-600 dark:text-teal-400',
    bgColor: 'bg-teal-50 dark:bg-teal-950',
    borderColor: 'border-teal-200 dark:border-teal-800',
  },
  update_line_item_product: {
    icon: PackageIcon,
    label: 'Match Product',
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-950',
    borderColor: 'border-indigo-200 dark:border-indigo-800',
  },
};

/**
 * Build the display label for an action, appending the SKU identifier
 * for line-item actions so the user can distinguish between items.
 */
function getActionLabel(action: SuggestedActionWithStatus): string {
  const config = actionTypeConfig[action.type];
  if (lineItemActionTypes.has(action.type) && action.lineItemIdentifier) {
    return `${config.label}: ${action.lineItemIdentifier}`;
  }
  return config.label;
}

export function SuggestedActionCard({
  action,
  onApply,
  onDismiss,
  onReviewDraft,
  isApplying = false,
  className,
}: SuggestedActionCardProps) {
  const config = actionTypeConfig[action.type];
  const Icon = config.icon;

  // Don't render applied or dismissed actions
  if (action.status !== 'pending') {
    return null;
  }

  // Check if action has required data to be executable
  const isActionExecutable = (): boolean => {
    if (action.type === 'update_line_item_product') {
      // Requires productVariantId or customerProductId to execute
      return !!(action.productVariantId || action.customerProductId);
    }
    return true;
  };

  const handlePrimaryAction = () => {
    if (action.type === 'send_email_draft' && onReviewDraft) {
      onReviewDraft(action);
    } else {
      onApply(action);
    }
  };

  const canExecute = isActionExecutable();

  const primaryActionLabel =
    action.type === 'send_email_draft'
      ? 'Review Draft'
      : action.type === 'update_line_item_product' && !canExecute
        ? 'Needs Selection'
        : action.type === 'approve_order'
          ? 'Approve and Create Sales Order'
          : 'Apply';

  return (
    <div
      className={cn(
        'relative flex items-start gap-3 rounded-lg border p-3',
        config.bgColor,
        config.borderColor,
        className,
      )}
    >
      <Button
        size="sm"
        variant="ghost"
        onClick={() => onDismiss(action)}
        disabled={isApplying}
        className="absolute right-1 top-1 h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
        aria-label="Dismiss"
      >
        <XIcon className="h-3.5 w-3.5" />
      </Button>
      <div
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
          config.bgColor,
        )}
      >
        <Icon className={cn('h-4 w-4', config.iconColor)} />
      </div>
      <div className="min-w-0 flex-1 pr-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            {getActionLabel(action)}
          </span>
        </div>
        <p className="mt-0.5 text-sm text-foreground">
          {action.type === 'approve_order'
            ? 'All required fields resolved'
            : action.description}
        </p>
        {action.type === 'update_line_item_price' &&
          action.newPrice !== undefined && (
            <p className="mt-1 text-xs text-muted-foreground">
              New price: ${action.newPrice.toFixed(2)}
            </p>
          )}
        {action.type === 'update_order_dates' &&
          (action.deliveryDate || action.shipDate) && (
            <p className="mt-1 text-xs text-muted-foreground">
              {action.deliveryDate && `Delivery: ${action.deliveryDate}`}
              {action.deliveryDate && action.shipDate && ' | '}
              {action.shipDate && `Ship: ${action.shipDate}`}
            </p>
          )}
        {action.type === 'update_ship_to_location' && action.locationName && (
          <p className="mt-1 text-xs text-muted-foreground">
            Location: {action.locationName}
          </p>
        )}
        {action.type === 'update_line_item_product' && action.productName && (
          <p className="mt-1 text-xs text-muted-foreground">
            Product: {action.productName}
            {action.productSku && ` (${action.productSku})`}
          </p>
        )}
        <div className="mt-2">
          <Button
            size="sm"
            variant={canExecute ? 'default' : 'secondary'}
            onClick={handlePrimaryAction}
            disabled={isApplying || !canExecute}
            className="h-7 px-2 text-xs"
            title={
              !canExecute
                ? 'This action requires customer confirmation to select the correct product'
                : undefined
            }
          >
            {isApplying ? (
              <CircleNotchIcon className="mr-1 h-3 w-3 animate-spin" />
            ) : null}
            {primaryActionLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
